<template>
<div
	v-if="isDataFetched"
	style="width: 100%; height: 100%;"
>
	<main-header
		v-if="sessionStore.isOpen"
		:organization_name="accountStore.organization.title"
		@signout="closeUserSession()"
	/>

	<side-menu
		v-if="sessionStore.isOpen"
		@signout="closeUserSession()"
	/>

	<main 
		v-if="sessionStore.isOpen"
		class="main-content"
		:class="{ 'main-content-pinned-open': sidebarModeStore === 'open-pinned' }"
		style="scroll-behavior: smooth;"
	>
		<router-view />
	</main>

	<SigninView
		v-if="!sessionStore.isOpen"
		@signin="openUserSession()"
	/>
</div>

<div
	v-if="!isDataFetched" 
	class="d-flex flex-column justify-content-center align-items-center h-100"
>
	<div class="spinner-border text-secondary app-spinner" role="status">
		<span class="visually-hidden">Loading...</span>
	</div>
	<div v-if="isLostConnection" class="mt-5 text-secondary text-center">
		<strong role="status">Схоже сервер не доступний зараз.</strong>
		<p>Триває відновлення зв'язку...</p>
	</div>
</div>
</template>




<script>
import { useLocalStorage } from '@vueuse/core'
import { CHECK_CONNECTION_PERIOD, APP_VERSION } from './app.conf'
import MainHeader from './components/MainHeader.vue'
import SideMenu from '@/components/Menu/SideMenu.vue'
import { useAccontStore } from './stores/AccountStore.js'
import { useSessionStore } from './stores/SessionStore.js'
import { useStationsStore } from './stores/StationsStore.js'
import { usePostsStore } from '@/stores/PostsStore.js'
import { useStationsHistoryStore } from './stores/StationsHistoryStore.js'
import { initInfluxApi } from '@/api/influxdb.js'
import { initGotifyApi } from '@/api/gotify.js'
import SigninView from './components/SigninView.vue'



export default {
	name: 'App',

	inject: ['headerHeight'],

	components: {
		MainHeader,
		SideMenu,
		SigninView,
	},

	setup() {
		console.info("App setup.")
		const accountStore = useAccontStore()
		const sessionStore = useSessionStore()
		const stationsStore = useStationsStore()
		const postsStore = usePostsStore()
		const stationHistoryStore = useStationsHistoryStore()
		const sidebarModeStore = useLocalStorage('sidebar-mode', 'mobile')

		return {
			accountStore,
			sessionStore,
			stationsStore,
			postsStore,
			stationHistoryStore,
			sidebarModeStore,
		}
	},

	data() {
		return {
			// Load site data flag
			isDataFetched: false,
			//
			isLostConnection: false,
			//
			isAccountLoaded: false,
		}
	},

	async created() {
		console.info(`App v${APP_VERSION} created.`)
		
		// On change page
		this.$watch(() => this.$route.path, (newVal, oldVal) => {
			// bugfix: prevent signout user on page update
			if (newVal == oldVal || oldVal == "/") {
				return
			}

			// Update last activity timestamp
			this.sessionStore.activity()
		})
	},

	async mounted() {
		console.info("App mounted.")

		await this.initUserAccount()
		
		if (!this.isAccountLoaded) {
			console.info('Account is not loaded')
			this.$router.push({ name: 'signin',})
			this.isDataFetched = true
			this.isLostConnection = false
		} else {
			console.info(`Account ${this.accountStore.userLogin} is loaded`)
		}

		const isSessionOpen = await this.sessionStore.tryContinueSession(
			this.accountStore.userId,
			this.accountStore.userLogin,
			this.accountStore.userPassword,
		)
		if (!isSessionOpen) {
			console.info('Session is not open, go to signin page')
			this.$router.push({ name: 'signin',})
			this.isDataFetched = true
			this.isLostConnection = false
		} else {
			console.info('Continue user session')
		}
	},

	methods: {
		async initUserAccount() {
			// Load Account
			this.isAccountLoaded = await this.accountStore.load()
			//console.log("isAccountLoaded = ", this.isAccountLoaded)
			
			// If no connection to server 
			if (!this.isAccountLoaded) {
				this.isLostConnection = true
				
				// Reload account function
				const reloadAccount = async (timerId) => {
					this.isAccountLoaded = await this.accountStore.load()
					//console.log('is account loaded = ', this.isAccountLoaded)
					if (this.isAccountLoaded) {
						this.isDataFetched = true
						this.isLostConnection = false
						clearInterval(timerId);
					} else {
						setTimeout(reloadAccount, CHECK_CONNECTION_PERIOD);
					}
				}

				// Periodicaly check konnection
				setTimeout(reloadAccount, CHECK_CONNECTION_PERIOD);
			}
			// If connect to server
			else {
				this._saveUserOrganizationId()
				this._saveUserViewScope()
				this._initMongoApi()
				this._initInfluxApi()
				this._initGotifyApi()
				this.isDataFetched = true
			}
		},

		async openUserSession() {
			await this.initUserAccount()

			if (this.isAccountLoaded) {
				await this.sessionStore.open(this.accountStore.userId, this.accountStore.userLogin, this.accountStore.userPassword,)
				console.info('User session is opened, go to stations')
				this.$router.push({ name: 'stations',})
			}
		},

		closeUserSession() {
			this.sessionStore.close()
			this.accountStore.cleanAccount()
			this.$router.push({ name: 'signin',})
			//window.location.reload();
		},

		_saveUserOrganizationId() {
			this.stationsStore.setOrganizationId(
				this.accountStore.isPrimary ?
				null :
				this.accountStore.organization._id
			)

			this.postsStore.setOrganizationId(
				this.accountStore.isPrimary ?
				null :
				this.accountStore.organization._id
			)
		},

		_saveUserViewScope() {
			this.stationsStore.setDepartmnetsId(this.accountStore.departmentsScope)
			this.stationsStore.setSectorsId(this.accountStore.sectorsScope)

			this.postsStore.setDepartmnetsId(this.accountStore.departmentsScope)
			this.postsStore.setSectorsId(this.accountStore.sectorsScope)
		},

		_initInfluxApi() {
			initInfluxApi(this.accountStore.organization[this.accountStore.schema.organization.influxApiKey])
		},

		_initMongoApi() {
			this.stationsStore.setApiAccessKeys(
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			)

			this.postsStore.setApiAccessKeys(
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			)

			this.stationHistoryStore.setApiAccessKeys(
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			)
		},

		_initGotifyApi() {
			initGotifyApi(
				this.accountStore.organization[this.accountStore.schema.organization.gotifyUser],
				this.accountStore.organization[this.accountStore.schema.organization.gotifyPass]
			)
		},
	},

	computed: {},
}
</script>




<style>
#app {
	width: 100%;
	height: 100%;
	font-family: Roboto, Helvetica, Arial, sans-serif;
	/* font-size:small; */
	font-size: 14px;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	color: #2c3e50;

	overflow: hidden;
}

html,
body {
  height: 100%;
  width: 100%;
  overflow: hidden;
}


.app-spinner {
	width: 7rem;
	height: 7rem;
}

.main-content {
	overflow-x: hidden;
	overflow-y: auto;
	/* Hide scroll bar, but while still being able to scroll */
	scrollbar-width: none;

	height: calc(100% - v-bind(headerHeight));
	transition: margin-left 0.2s ease-in-out;
}

/* On desktop the sidebar stays as a persistent 45px minimized strip,
   so offset the content to avoid overlap. */
@media (min-width: 992px) {
	.main-content {
		margin-left: 45px;
	}

	.main-content.main-content-pinned-open {
		margin-left: calc(14rem + 4px);
	}
}

/* Scrollbar style. Tiny gray scrollbar */
/* A new W3C standard that is currently only available in Firefox */
* {
   scrollbar-width: thin;
   scrollbar-color: rgb(133, 133, 133) rgb(240, 240, 240);
} 
 
/* Works in Chrome/Edge/Safari */
*::-webkit-scrollbar {
   width: 12px; 
} 
*::-webkit-scrollbar-track {
   background: rgb(133, 133, 133); 
} 
*::-webkit-scrollbar-thumb {
   background-color: rgb(240, 240, 240);
   border-radius: 20px;
   border: 3px solid rgb(133, 133, 133);
}

/* Adjust styles for correct shadows in easy-data-table fixed columns */
td.shadow {
  box-shadow: none !important;
}
th.shadow {
	box-shadow: none !important;
}
/* ------------------------------------------------------------------ */
/* Adjust styles for background in easy-data-table expandable rows */
.expand-row-no-background:hover td {
	/* background-color: --easy-table-body-row-background-color; */
	background-color: inherit !important;
}
/* .expand-row-no-background:hover tr.even-row {
	background-color: inherit !important;
	background-color: --easy-table-body-row-hover-background-color;
} */
.expand-row-no-background td {
	background-color: inherit !important;
	/* background-color: --easy-table-body-row-background-color; */
}
/* ------------------------------------------------------------------ */

.border-radius {
	border-radius: 4px;
}

/* An intendation in first line of paragraf */
.paragraph-indent {
	text-indent: 2em;
}
p.paragraph-indent {
	margin-top: 2px;
	margin-bottom: 4px;
}
</style>
